import {
  Font,
  Image,
  Link,
  Document as PDFDocument,
  Page,
  StyleSheet,
  type Styles,
  Text,
  View,
} from "@react-pdf/renderer";
import type {
  Address,
  LetterHead,
  Meeting,
  QuillDelta,
  Team,
  TeamMemberWContact,
} from "@repo/types";
import {
  convertDeltaToRichText,
  type RichTextLine,
  type RichTextSegment,
} from "@repo/shared/deltaToRichText";
import { Fragment, useMemo } from "react";
import { arrayify } from "@repo/shared/utils";
import { loadFonts } from "../fonts";

// #region Styles

type Styleset = Styles[""];

let fontsLoaded = false;
const readyPromise = loadFonts().then((fonts) => {
  fontsLoaded = true;

  Font.register({
    family: "SansSerif",
    fonts: [
      {
        src: fonts.sansNormal,
        fontWeight: "normal",
      },
      {
        src: fonts.sansBold,
        fontWeight: "bold",
      },
      {
        src: fonts.sansNormalItalic,
        fontWeight: "normal",
        fontStyle: "italic",
      },
      {
        src: fonts.sansBoldItalic,
        fontWeight: "bold",
        fontStyle: "italic",
      },
    ],
  });

  Font.register({
    family: "Serif",
    fonts: [
      {
        src: fonts.serifNormal,
        fontWeight: "normal",
      },
      {
        src: fonts.serifBold,
        fontWeight: "bold",
      },
      {
        src: fonts.serifNormalItalic,
        fontWeight: "normal",
        fontStyle: "italic",
      },
      {
        src: fonts.serifBoldItalic,
        fontWeight: "bold",
        fontStyle: "italic",
      },
    ],
  });

  Font.register({
    family: "Monospace",
    fonts: [
      {
        src: fonts.monospaceNormal,
        fontWeight: "normal",
      },
      {
        src: fonts.monospaceBold,
        fontWeight: "bold",
      },
      {
        src: fonts.monospaceNormalItalic,
        fontWeight: "normal",
        fontStyle: "italic",
      },
      {
        src: fonts.monospaceBoldItalic,
        fontWeight: "bold",
        fontStyle: "italic",
      },
    ],
  });
});

Font.registerHyphenationCallback((word) => [word]);

const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    padding: "36px 36px 60px",
    fontSize: 12,
    fontFamily: "SansSerif",
  },
  footer: {
    position: "absolute",
    bottom: 25,
    left: 36,
    right: 36,
    fontSize: 10,
  },
});

// #region C: Letterhead

interface LetterheadLogoProps {
  url: string | undefined;
  enableLinks: boolean;
}
/** Letterhead logo */
const LetterheadLogoBlock = ({ url }: LetterheadLogoProps) =>
  url ? (
    <Image
      src={url}
      style={{
        aspectRatio: "1:1",
        width: "100px",
        marginBottom: "25px",
      }}
    />
  ) : null;

interface LetterheadOfficerBlockProps {
  member: TeamMemberWContact;
  enableLinks: boolean;
}
/** Letterhead officer block */
const LetterheadOfficerBlock = ({ member }: LetterheadOfficerBlockProps) => (
  <View
    style={{
      fontSize: "8px",
      marginTop: "20px",
      textAlign: "center",
      width: "100%",
    }}
  >
    <Text>{`${member.contactInfo.firstName} ${member.contactInfo.lastName}`}</Text>
    <Text>{member.title}</Text>
    <Text>{arrayify(member.contactInfo.primaryPhone)[0]?.number}</Text>
    <Text>{member.contactInfo.email}</Text>
  </View>
);

interface LetterheadSidebarProps {
  letterHead?: LetterHead;
  enableLinks: boolean;
}
/** Left-hand letterhead sidebar */
const LetterheadSidebar = ({
  letterHead,
  enableLinks,
}: LetterheadSidebarProps) =>
  letterHead ? (
    <View
      style={{
        flexDirection: "column",
        width: "30%",
        alignItems: "center",
      }}
    >
      <LetterheadLogoBlock url={letterHead.logoUrl} enableLinks={enableLinks} />

      <Text style={{ fontSize: "10px", fontWeight: "bold" }}>
        Officers/Board:
      </Text>

      {letterHead.members.map((member, index) => (
        <LetterheadOfficerBlock
          key={index}
          member={member}
          enableLinks={enableLinks}
        />
      ))}
    </View>
  ) : null;

interface LetterheadDetailsBlockProps {
  letterHead: LetterHead;
  enableLinks: boolean;
  address?: Address;
}
/** Letterhead details heading */
const LetterheadDetailsBlock = ({
  letterHead,
  address,
}: LetterheadDetailsBlockProps) => (
  <View
    style={{
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      marginBottom: "24px",
    }}
  >
    <Text
      style={{
        fontSize: "16px",
        fontWeight: "bold",
        marginBottom: "8px",
      }}
    >
      {letterHead.name}
    </Text>

    {address && (
      <>
        {address.street ? (
          <Text style={{ fontSize: "10px" }}>{address.street}</Text>
        ) : null}

        {address.street2 ? (
          <Text style={{ fontSize: "10px" }}>{address.street2}</Text>
        ) : null}

        {address.city || address.state || address.zip ? (
          <Text style={{ fontSize: "10px" }}>
            {(address.city ? `${address.city}, ` : "") +
              `${address.state} ${address.zip}`}
          </Text>
        ) : null}
      </>
    )}

    {letterHead.email ? (
      <Text style={{ fontSize: "10px" }}>{`Email: ${letterHead.email}`}</Text>
    ) : null}

    {letterHead.website ? (
      <Text
        style={{ fontSize: "10px" }}
      >{`Website: ${letterHead.website}`}</Text>
    ) : null}
  </View>
);

// #region RichText

const Hr = () => (
  <View
    style={{
      borderBottom: "1px solid black",
      marginTop: 8,
      marginBottom: 13,
    }}
  />
);

const fontSize = 12;

const buildLineStyle = (lineAttrs: RichTextLine["attributes"]): Styleset => {
  const styles: Styleset & { fontSize: number } = {
    fontSize,
    lineHeight: 1.5,
    marginBottom: 3,
    position: "relative",
  };

  if (lineAttrs.align) {
    styles.textAlign = lineAttrs.align ?? "left";
  }
  if (lineAttrs.header) {
    styles.fontSize = {
      1: 2 * fontSize,
      2: 1.5 * fontSize,
      3: 1.17 * fontSize,
    }[lineAttrs.header as 1 | 2 | 3];
  }
  if (lineAttrs.indent) {
    styles.marginLeft = lineAttrs.indent * 39;
  }
  if (lineAttrs.list) {
    styles.paddingLeft = styles.fontSize * 3;
  }
  if (lineAttrs.blockquote) {
    styles.paddingLeft = 16;
    styles.borderLeft = "4px solid #cccccc";
  }

  return styles;
};

const buildSegmentStyle = (
  segmentAttrs: RichTextSegment["attributes"],
  parentFontSize: number
): Styleset => {
  const styles: Styleset = {};
  if (!segmentAttrs) return styles;

  if (segmentAttrs.size) {
    styles.fontSize =
      { small: 0.75, large: 1.5, huge: 2.5 }[segmentAttrs.size] *
      parentFontSize;
  }
  if (segmentAttrs.background) {
    styles.backgroundColor = segmentAttrs.background;
  }
  if (segmentAttrs.color) {
    styles.color = segmentAttrs.color;
  }
  if (segmentAttrs.bold) {
    styles.fontWeight = "bold";
  }
  if (segmentAttrs.italic) {
    styles.fontStyle = "italic";
  }
  if (segmentAttrs.underline || segmentAttrs.strike) {
    styles.textDecoration = (
      (segmentAttrs.underline ? "underline " : "") +
      (segmentAttrs.strike ? "line-through " : "")
    ).trim() as Styleset["textDecoration"];
  }
  if (segmentAttrs.font) {
    styles.fontFamily = { serif: "Serif", monospace: "Monospace" }[
      segmentAttrs.font
    ];
  }

  return styles;
};

const linkWrap = (jsx: React.ReactNode, url: string | undefined) => {
  if (!url) return jsx;
  return <Link href={url}>{jsx}</Link>;
};

interface RichTextProps {
  lines: RichTextLine[];
}
const RichText = ({ lines }: RichTextProps) => {
  let orderedListIndex = 0;

  return lines.map((line, index) => {
    if (
      line.segments.length === 1 &&
      typeof line.segments[0].content !== "string"
    ) {
      if (line.segments[0].content.divider) {
        return <Hr />;
      }
    }

    const lineStyle = buildLineStyle(line.attributes);

    if (line.attributes.list === "ordered") {
      orderedListIndex++;
    } else {
      orderedListIndex = 0;
    }

    return (
      <View style={{ position: "relative" }}>
        {line.attributes.list ? (
          <Text style={{ position: "absolute", left: 20 }}>
            {
              { bullet: "\u2022", ordered: orderedListIndex + "." }[
                line.attributes.list
              ]
            }
          </Text>
        ) : null}
        <Text key={index} style={lineStyle}>
          {line.segments.length ? (
            line.segments.map((segment, index) => (
              <Fragment key={index}>
                {typeof segment.content === "string"
                  ? linkWrap(
                      <Text
                        style={buildSegmentStyle(
                          segment.attributes,
                          lineStyle.fontSize as number
                        )}
                      >
                        {segment.content.replace(/ {2,}/g, (spaces) =>
                          spaces.replace(/ {2}/g, "\xa0 ")
                        )}
                      </Text>,
                      segment.attributes?.link
                    )
                  : null}
              </Fragment>
            ))
          ) : (
            <Text key={index}> </Text>
          )}
        </Text>
      </View>
    );
  });
};

// #region Document Root

export interface MeetingMinutesDocumentProps {
  meeting: Meeting;
  team?: Team;
  letterHead?: LetterHead;
  enableLinks?: boolean;
}

export const MeetingMinutesDocument = Object.assign(
  ({
    meeting,
    team,
    letterHead,
    enableLinks = true,
  }: MeetingMinutesDocumentProps) => {
    if (!fontsLoaded) throw new Error("Fonts not loaded");

    const richTextLines = useMemo(() => {
      const json = meeting.minutesData?.json;
      if (!json) return;

      const data = JSON.parse(json) as QuillDelta;
      const lines: RichTextLine[] = convertDeltaToRichText(data);

      return lines;
    }, [meeting]);

    const year = new Date().getFullYear();

    if (!richTextLines) return;

    return (
      <PDFDocument>
        <Page size="LETTER" style={styles.page}>
          {/* Letterhead logo and officers */}
          <LetterheadSidebar
            letterHead={letterHead}
            enableLinks={enableLinks}
          />

          <View style={{ flexDirection: "column", flexBasis: 1, flexGrow: 1 }}>
            {/* Letterhead title and details */}

            {!!letterHead && (
              <LetterheadDetailsBlock
                letterHead={letterHead}
                enableLinks={enableLinks}
                address={meeting.address ?? team?.address}
              />
            )}

            <View
              style={{
                flexDirection: "column",
              }}
            >
              {/* Meeting minutes details */}
              <RichText lines={richTextLines} />
            </View>
          </View>

          <Text
            style={{ ...styles.footer, textAlign: "right" }}
            render={({ pageNumber }) => `Page ${pageNumber}`}
            fixed
          />

          <Text
            style={{
              ...styles.footer,
              fontSize: 8,
              textAlign: "left",
            }}
            fixed
          >
            Powered by CiviLink Documents | {"\u00a9\ufe0f"} {year} CiviLink LLC
            | All Rights Reserved
          </Text>
        </Page>
      </PDFDocument>
    );
  },
  { readyPromise }
);
